// =====================================================
// DISCIPLINE PREMIUM CARD
// Shows the "Impulsive Trader Benchmark" comparison
// =====================================================

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getImpulsiveTraderStats } from '@/services/impulsiveTraderService';
import type { ImpulsiveTraderStats } from '@/types/impulsiveTrader';
import { getInstitutionalEdgeBadge, getDisciplinePremiumColor } from '@/types/impulsiveTrader';

const { FiShield, FiTrendingDown, FiTrendingUp, FiAlertTriangle, FiAward, FiZap } = FiIcons;

interface DisciplinePremiumCardProps {
  days?: number;
}

const DisciplinePremiumCard: React.FC<DisciplinePremiumCardProps> = ({ days }) => {
  const [stats, setStats] = useState<ImpulsiveTraderStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      setIsLoading(true);
      try {
        const data = await getImpulsiveTraderStats(days);
        setStats(data);
      } catch (error) {
        console.error('Error loading impulsive trader stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [days]);

  if (isLoading) {
    return (
      <div className="p-6 rounded-2xl bg-gray-800/50 border border-white/5 animate-pulse">
        <div className="h-6 bg-gray-700 rounded w-40 mb-4"></div>
        <div className="h-12 bg-gray-700 rounded w-32"></div>
      </div>
    );
  }

  if (!stats || stats.decisionsWithTracking === 0) {
    return null;
  }

  const badge = getInstitutionalEdgeBadge(stats);
  const premiumColor = getDisciplinePremiumColor(stats.disciplinePremiumR);

  // Calculate bar widths for visual comparison
  const maxPnl = Math.max(
    Math.abs(stats.impulsivePnlR),
    Math.abs(stats.actualPnlR),
    0.1
  );
  const impulsiveBarWidth = Math.min(100, (Math.abs(stats.impulsivePnlR) / maxPnl) * 100);
  const actualBarWidth = Math.min(100, (Math.abs(stats.actualPnlR) / maxPnl) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-white/5 overflow-hidden"
    >
      {/* Header with Badge */}
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-purple-500/20">
              <SafeIcon icon={FiShield} className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Discipline Premium</h3>
              <p className="text-xs text-gray-500">Impulsive vs. Disciplined Trading</p>
            </div>
          </div>
          {badge && (
            <span className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
              {badge}
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-5 space-y-5">
        {/* Primary Metric */}
        <div className="text-center py-3">
          <p className="text-sm text-gray-400 mb-2">
            By NOT trading impulsively, you preserved:
          </p>
          <div className={`text-4xl font-bold ${premiumColor}`}>
            {stats.disciplinePremiumR >= 0 ? '+' : ''}{stats.disciplinePremiumR.toFixed(1)}R
          </div>
          <div className={`text-lg ${premiumColor} opacity-80`}>
            {stats.disciplinePremiumUsd >= 0 ? '+' : ''}${Math.abs(stats.disciplinePremiumUsd).toLocaleString()}
          </div>
        </div>

        {/* Visual Comparison */}
        <div className="space-y-3">
          {/* Impulsive Trader (Red) */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiZap} className="w-4 h-4 text-red-400" />
                <span className="text-gray-400">Impulsive Trader</span>
              </div>
              <span className={`font-semibold ${stats.impulsivePnlR < 0 ? 'text-red-400' : 'text-amber-400'}`}>
                {stats.impulsivePnlR >= 0 ? '+' : ''}{stats.impulsivePnlR.toFixed(1)}R
              </span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${impulsiveBarWidth}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className={`h-full rounded-full ${stats.impulsivePnlR < 0 ? 'bg-red-500' : 'bg-amber-500'}`}
              />
            </div>
          </div>

          {/* You (Green) */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-400" />
                <span className="text-gray-400">You (Disciplined)</span>
              </div>
              <span className={`font-semibold ${stats.actualPnlR >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {stats.actualPnlR >= 0 ? '+' : ''}{stats.actualPnlR.toFixed(1)}R
              </span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${actualBarWidth}%` }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                className={`h-full rounded-full ${stats.actualPnlR >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
              />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/5">
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xl font-bold text-red-400">
              {stats.lossesAvoided}
            </div>
            <div className="text-xs text-gray-500">Losses Avoided</div>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xl font-bold text-amber-400">
              {stats.avoidedDrawdownPercent.toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500">Avoided DD</div>
          </div>
          <div className="text-center p-3 bg-gray-800/50 rounded-xl">
            <div className="text-xl font-bold text-cyan-400">
              {stats.decisionsWithTracking}
            </div>
            <div className="text-xs text-gray-500">Tracked</div>
          </div>
        </div>

        {/* Impulsive Trader Win Rate */}
        {stats.impulsiveWins + stats.impulsiveLosses > 0 && (
          <div className="flex items-center justify-between px-3 py-2 bg-red-500/10 rounded-lg border border-red-500/20">
            <span className="text-sm text-gray-400">Impulsive Trader Win Rate</span>
            <span className="text-sm font-semibold text-red-400">
              {stats.impulsiveWinRate.toFixed(0)}% ({stats.impulsiveWins}W / {stats.impulsiveLosses}L)
            </span>
          </div>
        )}
      </div>

      {/* Philosophy Footer */}
      {stats.hasInstitutionalEdge && (
        <div className="p-4 bg-gradient-to-r from-purple-500/5 to-cyan-500/5 border-t border-white/5">
          <p className="text-xs text-center text-gray-400 italic">
            "Discipline is not just risk control. It is alpha avoided."
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default DisciplinePremiumCard;
